import React,{useState, useEffect} from 'react';
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import Button,{btnSize} from "../../../components/UI/Button/Button.ui";
import {
    saveDemographics,
    fetchProfileConfirmation,
    confirmProfile,
    submitProfileChanges,
} from "../../../services/app_start.service";
import {useGlobalState} from "../../../context/global_state";
import Footer from "../../../components/Footer/plugable.footer";
import {LOCAL_STORAGE_KEYS, DQ_THEMES} from "../../../utils/constants";
import {useLocation} from "react-router-dom";
import {GET_APP_LOCALIZE_TEXT} from "../../../utils/dq_lc_service/lc_service";
import styled from "styled-components";
import LangDropdown from "../../DQIndex/components/LangDropdown.comp";
import {useCountries, useStates} from "../../../utils/geo";
import SearchableSelect from "../../../components/UI/SearchableSelect/SearchableSelect.ui";

/**
 * The first screen a learner sees after signing in — /:lc/demographic.
 *
 * It does two jobs, because they are the same moment:
 *
 *   1. Collects the demographics DQ World needs before an assessment can
 *      be scored (gender, year of birth, country, state, postal code).
 *      Unchanged: still saveDemographics, still the is_demographic_updated
 *      gate every other screen redirects on.
 *
 *   2. Shows the learner the details their SCHOOL entered for them — name,
 *      email, and whichever demographics came in on the class CSV — and
 *      lets them correct anything wrong.
 *
 * The split between the two is ownership, not field type:
 *
 *   · A field that was EMPTY when this screen loaded, nobody has claimed.
 *     The learner is filling it in for the first time and it saves
 *     immediately, exactly as this form has always behaved.
 *
 *   · A field the school already filled in belongs to the school. The
 *     learner changing it is a CORRECTION, and a correction is recorded as
 *     a request for the school to approve — it does not take effect here.
 *     That is the entire reason profilechangerequests exists: a learner
 *     cannot silently rewrite the roster their school built.
 *
 * Country and state are the exception to both: PBDQ-45 keeps them the
 * teacher's, so they are shown disabled and are never submitted either way.
 *
 * A self-registered learner (no school-provided values) sees precisely the
 * form that was here before — the school-owned rows render only when the
 * school actually set something.
 */

/**
 * The English of every string this screen added, by locale key.
 *
 * GET_APP_LOCALIZE_TEXT answers "**Missing Text**" — a truthy string — for a
 * key it does not know, so `LC(...) || fallback` cannot work and a key that is
 * missing for any reason (a stale bundle, a locale block that lost one) puts
 * that marker in front of the learner. This is the fallback instead.
 */
const PROFILE_CHECK_EN = {
    dq_profile_check__title: 'Check your details',
    dq_profile_check__subtitle: 'Your school added these. Fix anything that is wrong and we will send the corrections to them.',
    dq_profile_check__first_name: 'First name',
    dq_profile_check__last_name: 'Last name',
    dq_profile_check__student_email: 'My email',
    dq_profile_check__where_title: 'Where this goes.',
    dq_profile_check__where_body: 'Anything you change here goes to your school to approve — they see the old value next to the new one. Your profile keeps its current details until they say yes.',
    dq_profile_check__sent_title: 'Sent to your school',
    dq_profile_check__sent_body: 'Your school will review the changes you asked for. Until they approve them your profile keeps the details they entered, so nothing looks different yet.',
    dq_profile_check__ok_title: 'Details confirmed',
    dq_profile_check__ok_body: 'Thanks for checking. Everything your school entered was already right, so there is nothing waiting for them to approve.',
    dq_profile_check__is_this_you: 'Is this you?',
    dq_profile_check__is_this_you_sub: 'Your school added these details. Take a second to check them.',
    dq_profile_check__all_correct: 'Yes, this is all correct',
    dq_profile_check__needs_change: 'Something needs to change',
    dq_profile_check__complete: 'Continue',
    dq_profile_check__send: 'Send to my school',
    dq_profile_check__cancel: 'Cancel',
    dq_profile_check__subtitle_complete: 'Fix anything your school got wrong, and fill in what is still blank.',
    dq_demographic__gender: 'Gender',
    dq_demographic__year_of_birth: 'Year of Birth',
    dq_demographic__postal_code: 'Postal / ZIP code',
};

/** Localised text, or the English above when the key is not in the bundle. */
const LC = (lc, key) => {
    const text = GET_APP_LOCALIZE_TEXT(lc, key);
    return (!text || text === '**Missing Text**') ? (PROFILE_CHECK_EN[key] || '') : text;
};


const ProfileConflict = (props) => {

    const { history } = props;

    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];

    const [loading, setLoading] = useState(false);

    const { setToastShow, setToastMessage, profile, setProfile, settings } = useGlobalState();

    // Already collected (e.g. during registration) — don't ask again, just carry it forward on submit.
    const hasYOB = !!profile?.year_of_birth;

    // PBDQ-45 — the teacher sets each learner's country/state in the class CSV, so
    // one class can't be split across countries/states by learner input. When the
    // profile already carries them they are pre-filled and LOCKED here; learners
    // who were not created from a CSV (no country on file) still pick their own.
    const teacherCountry = String(profile?.country || '').trim();
    const teacherState   = String(profile?.state   || '').trim();

    /*
     * Editable, but not directly changeable.
     *
     * PBDQ-45 locked these because the teacher sets them in the class CSV and
     * one class must not end up split across countries. That guarantee is kept
     * a different way now: the learner may TYPE a correction, and it goes to
     * the school like every other school-owned field — the profile keeps the
     * teacher's value until they approve it. Nothing a learner does moves a
     * class between countries on its own.
     */
    const isCountryLocked = false;
    const isStateLocked   = false;

    const [formGender, setFormGender] = useState('');
    const [formGenderOther, setFormGenderOther] = useState('');
    const [formYOB, setFormYOB] = useState(hasYOB ? Number(profile.year_of_birth) : '');
    const [formCountry, setFormCountry] = useState(teacherCountry);
    const [formStateCode, setFormStateCode] = useState('');
    /*
     * Countries and the selected country's states come from the API's geo master, so
     * this form lists exactly what the school and admin portals list. A state's `name`
     * is English — what the profile stores — and its `label` is in the page language.
     * The country list falls back to the deprecated inline one below until it loads.
     */
    const apiCountries = useCountries(pageLc);
    const {states: stateOptions, loading: statesLoading} = useStates(formCountry, pageLc);
    const [formPostCode, setFormPostCode] = useState('');

    // The school's copy of the eight fields, as held when this screen loaded.
    // `original` is what decides ownership below, so it is never re-read after
    // the learner starts typing.
    const [original, setOriginal] = useState({});
    const [formFirstName, setFormFirstName] = useState('');
    const [formLastName, setFormLastName] = useState('');
    const [formStudentEmail, setFormStudentEmail] = useState('');


    /*
     * Which of the two approved steps is on screen. 'check' is "Is this you?",
     * the read-only list; 'edit' is "Fix what's wrong", the editable grid.
     * Only ever reached when the school actually provided details — a
     * self-registered learner has nothing to check and goes straight to the form.
     */
    const [mode, setMode] = useState('check');

    // The school's copy has been fetched (or has failed to). Until then this
    // page cannot know whether it is the plain demographic form or the profile
    // check, and rendering the guess first is the flash of the old form.
    const [ready, setReady] = useState(false);

    /*
     * Fetched rather than read out of global state: global state is whatever
     * login happened to cache, and half of this screen exists to show the
     * learner what their school holds RIGHT NOW.
     *
     * A failure here is not fatal — the demographic half still works, the
     * learner just sees the form as it was before this feature.
     */
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await fetchProfileConfirmation();
                if (!alive) return;
                const f = res?.data?.fields || {};

                /*
                 * Already answered, or still waiting on the school — either way the
                 * question has been put once and must not be put again. Being
                 * re-asked every time this URL is opened reads as the correction
                 * having failed.
                 *
                 * Guarded on demographic_updated as well: the home page sends a
                 * learner here whenever that is false, so redirecting without
                 * checking it would bounce them between the two screens forever.
                 */
                if (res?.data?.demographic_updated
                    && (res?.data?.already_confirmed || res?.data?.pending_request)) {
                    history.replace(`/${pageLc}/home`);
                    return;
                }
                setOriginal(f);

                if (f.first_name) setFormFirstName(f.first_name);
                if (f.last_name) setFormLastName(f.last_name);
                if (f.student_email) setFormStudentEmail(f.student_email);
                if (f.postal_code) setFormPostCode(f.postal_code);

                /*
                 * Country and state come from HERE, not from the useState
                 * initialisers above.
                 *
                 * Those run once, on the first render, against a `profile` that
                 * may not have loaded — and a useState default cannot be revised
                 * afterwards. The screen would then show the school's country
                 * while the form field behind it held nothing, and the
                 * required-field check failed on a value the learner could see.
                 */
                // The state is matched to the dropdown once that country's states
                // have loaded — see the effect below.
                if (f.country) setFormCountry(f.country);
                if (f.year_of_birth) setFormYOB(Number(f.year_of_birth));
                if (f.gender) {
                    // Anything outside the two options is a free-text answer,
                    // which this form stores under "other".
                    const g = String(f.gender).toLowerCase();
                    if (g === 'male' || g === 'female') {
                        setFormGender(g);
                    } else {
                        setFormGender('other');
                        setFormGenderOther(f.gender);
                    }
                }
            } catch (e) {
                // deliberately silent — see above
            } finally {
                if (alive) setReady(true);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * The school's state as a dropdown pick, once its country's states have loaded.
     * By code OR by name: a class CSV may carry either — "AD-07" as often as
     * "Andorra la Vella" — and state_code is only set when the value came from this
     * form in the first place. Runs once; a learner's own pick is never overwritten,
     * and nothing is matched once they have moved to another country.
     */
    const [stateMatched, setStateMatched] = useState(false);
    useEffect(() => {
        if (!ready || stateMatched || statesLoading) return;
        const src = original.country
            ? original
            : {country: teacherCountry, state: teacherState, state_code: profile?.state_code};
        if (String(src.country || '').trim() === formCountry) {
            const stored = String(src.state || '').trim();
            const code = String(src.state_code || '').trim();
            const match = stateOptions.find((o) => o.code === code)
                || stateOptions.find((o) => o.code === stored || o.name === stored || o.label === stored);
            if (match) setFormStateCode((picked) => picked || match.code);
        }
        setStateMatched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, statesLoading, stateOptions, formCountry]);

    /** Did the school fill this field in? Then it is theirs to change. */
    const wasSetBySchool = (key) => String(original[key] ?? '').trim() !== '';

    // Only a school-created learner has name/email on file. For everyone else
    // this screen stays exactly the demographic form it always was.
    const isSchoolProvided =
        wasSetBySchool('first_name') || wasSetBySchool('last_name') || wasSetBySchool('student_email');

    /*
     * The four the assessment cannot score without. The class CSV carries them
     * only if the school filled those columns in, so a roster that skipped them
     * leaves the learner with blanks they must complete before going on.
     *
     * When that is the case "Yes, this is all correct" cannot be offered — there
     * is nothing to agree to yet — so step 1 sends them to the form instead.
     */
    const needsCompletion =
        ['gender', 'year_of_birth', 'country', 'postal_code'].some((k) => !wasSetBySchool(k));


    /**
     * Where the learner goes once their demographics are saved. Unchanged
     * rules — pulled out of the submit handler only so the "sent to your
     * school" panel can send them on with the same ones.
     */
    const continueAfterSave = (dqw_access) => {
        const redirect_link_override_val = localStorage.getItem(LOCAL_STORAGE_KEYS.POST_DEMOGRAPHIC_REDIRECT_LINK);
        if (redirect_link_override_val) {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.POST_DEMOGRAPHIC_REDIRECT_LINK);
            history.push(redirect_link_override_val);
            return;
        }
        if (!dqw_access) {
            history.push('/home');
        } else {
            history.push('/select_platform');
        }
    };

    const submitDemographicFrom = async () => {

        // check if the form is filled completely
        if ([formGender === 'other' ? formGenderOther : formGender, formYOB, formCountry, formPostCode].includes('')) {
            setToastShow(true);
            setToastMessage(GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__plz_fill_req_fields'));
            return;
        }

        /*
         * State is required only when the country actually has subdivisions AND
         * the learner is the one choosing it.
         *
         * A school that supplied a state this dropdown cannot match — a spelling
         * of its own, or a code from another standard — must not dead-end the
         * learner on a field they never touched. So the check is skipped while
         * they are still on the school's country: they are agreeing to what is
         * on file, not entering something new. Pick a different country and it
         * applies again, because then the state genuinely is theirs to give.
         */
        const schoolState = String(original.state ?? '').trim();
        const onSchoolCountry = formCountry === String(original.country ?? '').trim();
        const schoolStateUnmatched = !!schoolState && onSchoolCountry;

        // While the country's states are still loading, whether one is needed is unknown.
        if (statesLoading || (!schoolStateUnmatched && stateOptions.length > 0 && formStateCode === '')) {
            setToastShow(true);
            setToastMessage(GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__plz_fill_req_fields'));
            return;
        }

        if (formYOB < MIN_BIRTH_YEAR || formYOB > maxBirthYear) {
            setToastShow(true);
            setToastMessage(`Birth year must be between ${MIN_BIRTH_YEAR} and ${maxBirthYear}.`);
            return;
        }

        // The save, the request and the redirect are three round trips. Without
        // this the screen sits there looking untouched and the learner presses
        // Next again.
        setLoading(true);

        let continue_home = false;
        let dqw_access = false;
        try {
            const selectedState = stateOptions.find(s => s.code === formStateCode);
            // Keep the teacher's state name when it isn't one of the dropdown codes,
            // so a locked value is preserved rather than blanked on submit.
            const stateName = selectedState ? selectedState.name : (isStateLocked ? teacherState : '');
            const genderValue = formGender === 'other' ? formGenderOther : formGender;

            /*
             * What the learner typed, keyed by the UserProfile column it would
             * land in — the same eight keys the API allows on a request.
             * Country and state are absent on purpose (PBDQ-45).
             */
            const entered = {
                first_name: formFirstName.trim(),
                last_name: formLastName.trim(),
                student_email: formStudentEmail.trim(),
                gender: genderValue,
                year_of_birth: String(formYOB || ''),
                postal_code: String(formPostCode).trim(),
                /*
                 * Country and state are corrections too. Sending the ORIGINAL to
                 * saveDemographics below is what keeps PBDQ-45's guarantee: the
                 * class stays in the country the teacher set until the school
                 * approves the change.
                 */
                country: String(formCountry || '').trim(),
                state: stateName,
            };

            /*
             * Everything the learner altered OR supplied.
             *
             * Both go to the school. A correction needs their approval; a value
             * they left blank in Add a Class needs only to be seen — the server
             * tells the two apart by whether anything was on file, and marks the
             * second kind as already applied.
             */
            const changes = {};
            // Gender is compared case-insensitively: the school CSV and this
            // form disagree on casing often enough that a straight compare would
            // queue "Female → female" for a school to approve.
            /*
             * State is compared by NAME on both sides.
             *
             * This form submits the name; a class CSV often carries the ISO
             * 3166-2 code. Comparing them raw would read "AE-AZ" against "Abu
             * Dhabi" as a change and queue a correction the learner never made,
             * every single time they confirmed their details.
             */
            const canonState = (v) => {
                const t = String(v ?? '').trim();
                if (!t) return '';
                // The selected country's states: a different country is a change anyway.
                const byCode = stateOptions.find((st) => st.code === t);
                return byCode ? byCode.name : t;
            };

            const same = (key, value) => {
                const was = String(original[key] ?? '').trim();
                if (key === 'gender') return value.toLowerCase() === was.toLowerCase();
                if (key === 'state') return canonState(value) === canonState(was);
                return value === was;
            };

            Object.keys(entered).forEach((key) => {
                if (same(key, entered[key])) return;
                changes[key] = entered[key];
            });

            /*
             * BEFORE the save, deliberately.
             *
             * The server works out what changed by comparing against the stored
             * profile, and the save below writes the learner's own value for
             * every field the school left blank. Running it first would leave
             * the profile already holding what the learner typed, the diff would
             * come back empty, and a first fill would never reach the school.
             *
             * Still best-effort: the demographic gate passes on the save below
             * regardless, so a failure here must not strand the learner. The
             * message is held and shown after, because App.js renders <Toast />
             * outside the Router and it has to survive the redirect.
             */
            let queuedToast = '';
            if (isSchoolProvided) {
                try {
                    if (Object.keys(changes).length) {
                        await submitProfileChanges(changes);
                        queuedToast = LC(pageLc, 'dq_profile_check__sent_title');
                    } else {
                        // Nothing wrong: record that they were asked, so the school
                        // can tell "checked and correct" from "never looked".
                        await confirmProfile();
                        queuedToast = LC(pageLc, 'dq_profile_check__ok_title');
                    }
                } catch (e) {
                    // invokeApi rethrows the server's own message, and here it is the
                    // useful one — "you already have a change waiting" tells the learner
                    // something the generic failure text cannot.
                    queuedToast = e?.message || GET_APP_LOCALIZE_TEXT(pageLc, 'uo__save_failed_try_again');
                }
            }

            /*
             * The ORIGINAL value for anything the school already holds — a
             * correction to one of those is only a request until they approve it,
             * so writing it here would apply the change the school has yet to see.
             *
             * A field the school left BLANK is written through, because this
             * endpoint requires gender, year_of_birth, country and postal_code to
             * be non-empty and posting an empty one fails the save outright. It is
             * still queued for the school, and rejecting it clears the field again
             * — see revertRejectedFirstFills on the server.
             */
            const result = await saveDemographics(
                wasSetBySchool('gender') ? original.gender : genderValue,
                wasSetBySchool('year_of_birth') ? original.year_of_birth : formYOB,
                wasSetBySchool('country') ? original.country : formCountry,
                wasSetBySchool('postal_code') ? original.postal_code : formPostCode,
                wasSetBySchool('state') ? original.state : stateName,
                wasSetBySchool('state') ? '' : formStateCode
            );

            // start block_41 :: calculate the current user age form year of birth and store in local-storage
            const currentTime = new Date()
            const current_year = currentTime.getFullYear();
            // The year actually SAVED, not the one typed — a corrected year of
            // birth is only a request until the school approves it, so the age
            // cached here must match the profile the server now holds.
            const saved_yob = wasSetBySchool('year_of_birth') ? Number(original.year_of_birth) : Number(formYOB);
            const year_diff = current_year - saved_yob;
            localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER_AGE, year_diff);
            // end block_41

            if (result.data && result.data.profile) {
                setProfile({
                    ...profile,
                    ...result.data.profile
                });
                // saveProfile() in App.js is commented-out, so AVAILABLE_PROFILES in
                // localStorage keeps the old is_demographic_updated=false value.
                // On hard refresh Home.page.jsx reads that stale value and redirects back here.
                // Fix: update only that one field so the cached profile matches the server.
                try {
                    const oc = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
                    const saved = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES) || '[]');
                    const entry = saved.find(p => p.org_code === oc);
                    if (entry) {
                        entry.is_demographic_updated = true;
                        localStorage.setItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES, JSON.stringify(saved));
                    }
                } catch (_) {}
                dqw_access = result.data.dqw_access;
            }

            /*
             * Best-effort, and deliberately after the save: the demographic gate
             * has already passed by this point, so a failure to lodge the
             * corrections must not strand the learner on a screen they can no
             * longer get past.
             */
            // Raised before the save — see there. Shown now so it survives the
            // redirect and the learner reads it on the dashboard.
            if (queuedToast) {
                setToastShow(true);
                setToastMessage(queuedToast);
            }

            continue_home = true;
        } catch (e) {
            setToastShow(true);
            setToastMessage(GET_APP_LOCALIZE_TEXT(pageLc, 'uo__save_failed_try_again'))
            // Stays on this screen, so the loader has to come back off.
            setLoading(false);
        }

        if (continue_home) {
            continueAfterSave(dqw_access);
        }

    }

    const onChangeGender = (e) => {
        setFormGender(e.target.value.trim());
    }

    const onChangeGenderOther = (e) => {
        setFormGenderOther(e.target.value.trim());
    }

    const onChangeYOB = (e) => {
        const selectedYear = Number(e.target.value);

        if (!selectedYear) {
            setFormYOB('');
            return;
        }

        if (selectedYear < MIN_BIRTH_YEAR || selectedYear > maxBirthYear) {
            setToastShow(true);
            setToastMessage(`Birth year must be between ${MIN_BIRTH_YEAR} and ${maxBirthYear}.`);
            return;
        }

        setFormYOB(selectedYear);
    };

    const handleCountryChange = (countryCode) => {
        setFormCountry(countryCode);
        // the state dropdown reloads for the new country (useStates); clear any prior pick
        setFormStateCode('');
    }

    const handleStateChange = (stateCode) => {
        setFormStateCode(stateCode);
    }

    const onChangePostCode = (e) => {
        setFormPostCode(e.target.value.trim());
    }
    const MIN_AGE = 8;
    const MIN_BIRTH_YEAR = 1920;

    const currentYear = new Date().getFullYear();
    const maxBirthYear = currentYear - MIN_AGE;

    // Last 99 years starting from current year - 8 (min user age = 8 years)
    const birthYears = Array.from({length: 99},(_,index) => currentYear-index-8 );

    // localized + alphabetized state labels for the currently selected country
    const localizedStates = stateOptions
        .map((st) => ({ code: st.code, label: st.label }))
        .sort((a, b) => a.label.localeCompare(b.label));

    /**
     * @deprecated Countries (and their translated names) now come from the API's geo
     * master: GET /v1/meta/countries?locale=xx → [{ code, name, dial_code, has_states }].
     * This list is out of date (it still has the retired AN / CS and lacks RS, ME, SS…);
     * kept, with its dq_country__* keys, only as the fallback while that list loads or
     * if it cannot be reached.
     */
    const countries = [
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__afghanistan'), "code": "AF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__aland_islands'), "code": "AX"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__albania'), "code": "AL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__algeria'), "code": "DZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__american_samoa'), "code": "AS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__andorra'), "code": "AD"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__angola'), "code": "AO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__anguilla'), "code": "AI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__antarctica'), "code": "AQ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__antigua_and_barbuda'), "code": "AG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__argentina'), "code": "AR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__armenia'), "code": "AM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__aruba'), "code": "AW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__australia'), "code": "AU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__austria'), "code": "AT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__azerbaijan'), "code": "AZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bahamas'), "code": "BS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bahrain'), "code": "BH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bangladesh'), "code": "BD"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__barbados'), "code": "BB"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__belarus'), "code": "BY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__belgium'), "code": "BE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__belize'), "code": "BZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__benin'), "code": "BJ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bermuda'), "code": "BM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bhutan'), "code": "BT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bolivia'), "code": "BO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bosnia_and_herzegovina'), "code": "BA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__botswana'), "code": "BW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bouvet_island'), "code": "BV"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__brazil'), "code": "BR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__british_indian_ocean_territory'), "code": "IO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__brunei_darussalam'), "code": "BN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__bulgaria'), "code": "BG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__burkina_faso'), "code": "BF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__burundi'), "code": "BI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cambodia'), "code": "KH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cameroon'), "code": "CM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__canada'), "code": "CA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cape_verde'), "code": "CV"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cayman_islands'), "code": "KY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__central_african_republic'), "code": "CF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__chad'), "code": "TD"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__chile'), "code": "CL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__china'), "code": "CN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__christmas_island'), "code": "CX"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cocos_keeling_islands'), "code": "CC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__colombia'), "code": "CO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__comoros'), "code": "KM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__congo'), "code": "CG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__congo_the_democratic_republic_of_the'), "code": "CD"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cook_islands'), "code": "CK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__costa_rica'), "code": "CR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cote_divoire'), "code": "CI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__croatia'), "code": "HR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cuba'), "code": "CU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__cyprus'), "code": "CY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__czech_republic'), "code": "CZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__denmark'), "code": "DK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__djibouti'), "code": "DJ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__dominica'), "code": "DM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__dominican_republic'), "code": "DO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__ecuador'), "code": "EC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__egypt'), "code": "EG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__el_salvador'), "code": "SV"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__equatorial_guinea'), "code": "GQ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__eritrea'), "code": "ER"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__estonia'), "code": "EE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__ethiopia'), "code": "ET"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__falkland_islands_malvinas'), "code": "FK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__faroe_islands'), "code": "FO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__fiji'), "code": "FJ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__finland'), "code": "FI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__france'), "code": "FR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__french_guiana'), "code": "GF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__french_polynesia'), "code": "PF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__french_southern_territories'), "code": "TF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__gabon'), "code": "GA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__gambia'), "code": "GM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__georgia'), "code": "GE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__germany'), "code": "DE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__ghana'), "code": "GH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__gibraltar'), "code": "GI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__greece'), "code": "GR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__greenland'), "code": "GL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__grenada'), "code": "GD"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__guadeloupe'), "code": "GP"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__guam'), "code": "GU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__guatemala'), "code": "GT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__guernsey'), "code": "GG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__guinea'), "code": "GN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__guinea_bissau'), "code": "GW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__guyana'), "code": "GY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__haiti'), "code": "HT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__heard_island_and_mcdonald_islands'), "code": "HM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__holy_see_vatican_city_state'), "code": "VA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__honduras'), "code": "HN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__hong_kong'), "code": "HK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__hungary'), "code": "HU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__iceland'), "code": "IS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__india'), "code": "IN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__indonesia'), "code": "ID"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__iran_islamic_republic_of'), "code": "IR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__iraq'), "code": "IQ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__ireland'), "code": "IE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__isle_of_man'), "code": "IM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__israel'), "code": "IL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__italy'), "code": "IT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__jamaica'), "code": "JM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__japan'), "code": "JP"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__jersey'), "code": "JE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__jordan'), "code": "JO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__kazakhstan'), "code": "KZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__kenya'), "code": "KE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__kiribati'), "code": "KI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__korea_democratic_peoples_republic_of'), "code": "KP"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__korea_republic_of'), "code": "KR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__kuwait'), "code": "KW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__kyrgyzstan'), "code": "KG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__lao_peoples_democratic_republic'), "code": "LA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__latvia'), "code": "LV"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__lebanon'), "code": "LB"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__lesotho'), "code": "LS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__liberia'), "code": "LR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__libyan_arab_jamahiriya'), "code": "LY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__liechtenstein'), "code": "LI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__lithuania'), "code": "LT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__luxembourg'), "code": "LU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__macao'), "code": "MO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__macedonia_the_former_yugoslav_republic_of'), "code": "MK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__madagascar'), "code": "MG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__malawi'), "code": "MW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__malaysia'), "code": "MY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__maldives'), "code": "MV"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__mali'), "code": "ML"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__malta'), "code": "MT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__marshall_islands'), "code": "MH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__martinique'), "code": "MQ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__mauritania'), "code": "MR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__mauritius'), "code": "MU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__mayotte'), "code": "YT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__mexico'), "code": "MX"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__micronesia_federated_states_of'), "code": "FM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__moldova_republic_of'), "code": "MD"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__monaco'), "code": "MC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__mongolia'), "code": "MN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__montserrat'), "code": "MS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__morocco'), "code": "MA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__mozambique'), "code": "MZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__myanmar'), "code": "MM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__namibia'), "code": "NA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__nauru'), "code": "NR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__nepal'), "code": "NP"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__netherlands'), "code": "NL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__netherlands_antilles'), "code": "AN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__new_caledonia'), "code": "NC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__new_zealand'), "code": "NZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__nicaragua'), "code": "NI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__niger'), "code": "NE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__nigeria'), "code": "NG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__niue'), "code": "NU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__norfolk_island'), "code": "NF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__northern_mariana_islands'), "code": "MP"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__norway'), "code": "NO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__oman'), "code": "OM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__pakistan'), "code": "PK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__palau'), "code": "PW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__palestinian_territory_occupied'), "code": "PS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__panama'), "code": "PA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__papua_new_guinea'), "code": "PG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__paraguay'), "code": "PY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__peru'), "code": "PE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__philippines'), "code": "PH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__pitcairn'), "code": "PN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__poland'), "code": "PL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__portugal'), "code": "PT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__puerto_rico'), "code": "PR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__qatar'), "code": "QA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__reunion'), "code": "RE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__romania'), "code": "RO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__russian_federation'), "code": "RU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__rwanda'), "code": "RW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__saint_helena'), "code": "SH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__saint_kitts_and_nevis'), "code": "KN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__saint_lucia'), "code": "LC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__saint_pierre_and_miquelon'), "code": "PM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__saint_vincent_and_the_grenadines'), "code": "VC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__samoa'), "code": "WS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__san_marino'), "code": "SM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__sao_tome_and_principe'), "code": "ST"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__saudi_arabia'), "code": "SA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__senegal'), "code": "SN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__serbia_and_montenegro'), "code": "CS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__seychelles'), "code": "SC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__sierra_leone'), "code": "SL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__singapore'), "code": "SG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__slovakia'), "code": "SK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__slovenia'), "code": "SI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__solomon_islands'), "code": "SB"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__somalia'), "code": "SO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__south_africa'), "code": "ZA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__south_georgia_and_the_south_sandwich_islands'), "code": "GS"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__spain'), "code": "ES"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__sri_lanka'), "code": "LK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__sudan'), "code": "SD"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__suriname'), "code": "SR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__svalbard_and_jan_mayen'), "code": "SJ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__swaziland'), "code": "SZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__sweden'), "code": "SE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__switzerland'), "code": "CH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__syrian_arab_republic'), "code": "SY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__taiwan_province_of_china'), "code": "TW"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__tajikistan'), "code": "TJ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__tanzania_united_republic_of'), "code": "TZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__thailand'), "code": "TH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__timor_leste'), "code": "TL"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__togo'), "code": "TG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__tokelau'), "code": "TK"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__tonga'), "code": "TO"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__trinidad_and_tobago'), "code": "TT"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__tunisia'), "code": "TN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__turkey'), "code": "TR"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__turkmenistan'), "code": "TM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__turks_and_caicos_islands'), "code": "TC"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__tuvalu'), "code": "TV"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__uganda'), "code": "UG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__ukraine'), "code": "UA"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__united_arab_emirates'), "code": "AE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__united_kingdom'), "code": "GB"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__united_states'), "code": "US"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__united_states_minor_outlying_islands'), "code": "UM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__uruguay'), "code": "UY"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__uzbekistan'), "code": "UZ"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__vanuatu'), "code": "VU"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__venezuela'), "code": "VE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__viet_nam'), "code": "VN"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__virgin_islands_british'), "code": "VG"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__virgin_islands_us'), "code": "VI"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__wallis_and_futuna'), "code": "WF"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__western_sahara'), "code": "EH"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__yemen'), "code": "YE"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__zambia'), "code": "ZM"},
        {"name": GET_APP_LOCALIZE_TEXT(pageLc, 'dq_country__zimbabwe'), "code": "ZW"},
    ];

    const countryOptions = (apiCountries || countries).map((c) => ({ value: c.code, label: c.name }));
    const stateSelectOptions = localizedStates.map((s) => ({ value: s.code, label: s.label }));

    const LogoImg = styled.img`
        content: url(${props => props.theme.logo});
    `

    /*
     * First frame. isSchoolProvided is false until the fetch lands, so without
     * this the learner sees the four-field demographic form blink into the
     * eight-field check — which is exactly what it looks like: a bug.
     */
    if (!ready) {
        return (
            <>
                <div className={'lang-dropdown-box'}>
                    <LangDropdown className={'imenu-language'} />
                </div>
                <div className={'register-container dq-demographic'}>
                    <LoaderWithBackDrop loading={true} />
                </div>
            </>
        );
    }


    /*
     * Step 1 of the approved flow — the learner reads what their school entered
     * and says whether it is right. Read-only on purpose: the point is that a
     * learner whose details are correct does nothing at all.
     *
     * "Yes, this is all correct" runs the ordinary submit with the form
     * untouched, so the demographics are still saved and the gate still closes —
     * agreeing is not the same as skipping.
     */
    if (isSchoolProvided && mode === 'check') {
        /** The school's value, as a person reads it. Codes become names. */
        const shown = (key) => {
            const raw = String(original[key] ?? '').trim();
            if (!raw) return '—';
            if (key === 'country') {
                return countryOptions.find((c) => c.value === raw)?.label || raw;
            }
            if (key === 'state') {
                /*
                 * A class CSV may carry the ISO 3166-2 code rather than the name —
                 * "AE-AZ" for Abu Dhabi. The code carries its country in the
                 * prefix; the screen opens on the school's country, so its states
                 * are the ones loaded. A state stored as its English name shows in
                 * the page language too; anything else is shown as it stands.
                 */
                const match = stateOptions.find((st) => st.code === raw || st.name === raw);
                return match ? match.label : raw;
            }
            if (key === 'gender') {
                if (raw.toLowerCase() === 'male') return GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__male');
                if (raw.toLowerCase() === 'female') return GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__female');
            }
            return raw;
        };

        const CHECK_ROWS = [
            ['first_name', LC(pageLc, 'dq_profile_check__first_name')],
            ['last_name', LC(pageLc, 'dq_profile_check__last_name')],
            ['year_of_birth', GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__year_of_birth')],
            ['gender', GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__gender')],
            ['country', GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__country')],
            ['state', GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__state')],
            ['postal_code', GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__postal_code')],
            ['student_email', LC(pageLc, 'dq_profile_check__student_email')],
        ];

        return (
            <>
                <div className={'lang-dropdown-box'}>
                    <LangDropdown className={'imenu-language'} />
                </div>
                <div className={'register-container dq-demographic dq-demographic-wide'}>
                    <LoaderWithBackDrop loading={loading} />
                    <div className="dq-logo-image">
                        <LogoImg className={`logo-${settings.theme.toLowerCase()}`} style={{objectFit: 'contain'}}></LogoImg>
                    </div>

                    <div className={'dq-demographic-intro'} style={{ marginBottom: 14 }}>
                        <h3 style={{ margin: 0 }}>
                            {LC(pageLc, 'dq_profile_check__is_this_you')}
                        </h3>
                        <p style={{ margin: 0, opacity: 0.75, fontSize: 14, lineHeight: 1.5 }}>
                            {LC(pageLc, 'dq_profile_check__is_this_you_sub')}
                        </p>
                    </div>

                    <div className={'dq-check-list'}>
                        {CHECK_ROWS.map(([key, label]) => (
                            <div className={'dq-check-row'} key={key}>
                                <span>{label}</span>
                                <strong>{shown(key)}</strong>
                            </div>
                        ))}
                    </div>

                    <div className={'dq-action-row'}>
                        <div className={'dq-options-submit'}>
                            {/* Nothing to agree to while required details are still
                                blank, so the same button takes them to the form. */}
                            <button onClick={() => (needsCompletion
                                ? setMode('edit')
                                : submitDemographicFrom())}>
                                {LC(pageLc, needsCompletion
                                    ? 'dq_profile_check__complete'
                                    : 'dq_profile_check__all_correct')}
                            </button>
                        </div>
                        <div className={'dq-options-submit dq-secondary-action'}>
                            <button onClick={() => setMode('edit')}>
                                {LC(pageLc, 'dq_profile_check__needs_change')}
                            </button>
                        </div>
                    </div>

                    <Footer />
                </div>
            </>
        );
    }

    return (
        <>
            <div className={'lang-dropdown-box'}>
                <LangDropdown className={'imenu-language'} />
            </div>
            <div className={`register-container dq-demographic${isSchoolProvided ? ' dq-demographic-wide' : ''}`}>
                {/* <section>
                    <header className={'header'}>
                        <div className={'toggle'}></div>
                        <img  src={headerImg} />
                    </header>
                </section> */}

                <LoaderWithBackDrop
                    loading={loading}
                />
                <div className="dq-logo-image">
                    <LogoImg className={`logo-${settings.theme.toLowerCase()}`} style={{objectFit: 'contain',}}></LogoImg>
                </div>
                {/*<div className="dq-logo-image"><img src={headerImg} alt='DQ'/></div>*/}

                {isSchoolProvided && (
                    <div className={'dq-demographic-intro'} style={{ marginBottom: 14 }}>
                        <h3 style={{ margin: 0 }}>
                            {LC(pageLc, 'dq_profile_check__title')}
                        </h3>
                        <p style={{ margin: 0, opacity: 0.75, fontSize: 14, lineHeight: 1.5 }}>
                            {LC(pageLc, needsCompletion
                                ? 'dq_profile_check__subtitle_complete'
                                : 'dq_profile_check__subtitle')}
                        </p>
                    </div>
                )}

                <form onSubmit={e => e.preventDefault()}>
                {/* Two columns once the school has filled anything in — eight
                    stacked rows is a scroll, not a form. Falls back to one
                    column under 700px, and to no grid at all for a learner with
                    nothing on file. */}
                <div className={isSchoolProvided ? 'dq-demographic-grid' : ''}>
                    {/* The school's own columns. Rendered only when the school set
                        them, so a self-registered learner sees the form unchanged.
                        Editable, but editing raises a request rather than saving. */}
                    {isSchoolProvided && (
                    <>
                    <div className='dq-register-row'>
                        <label>{LC(pageLc, 'dq_profile_check__first_name')}</label>
                        <input className={'dq-register-txt'}
                            type='text'
                            name={'first_name'}
                            id={'first_name'}
                            value={formFirstName}
                            onChange={(e) => setFormFirstName(e.target.value)}
                        />
                    </div>
                    <div className='dq-register-row'>
                        <label>{LC(pageLc, 'dq_profile_check__last_name')}</label>
                        <input className={'dq-register-txt'}
                            type='text'
                            name={'last_name'}
                            id={'last_name'}
                            value={formLastName}
                            onChange={(e) => setFormLastName(e.target.value)}
                        />
                    </div>
                    <div className='dq-register-row'>
                        <label>{LC(pageLc, 'dq_profile_check__student_email')}</label>
                        <input className={'dq-register-txt'}
                            type='email'
                            name={'student_email'}
                            id={'student_email'}
                            value={formStudentEmail}
                            onChange={(e) => setFormStudentEmail(e.target.value)}
                        />
                    </div>
                    </>
                    )}
                    <div className='dq-register-row'>
                        <label>{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__gender')}</label>
                        <div className={'dq-select'}>
                            <select className={'dq-register-txt'} name={'gender'} id={'gender'} value={formGender} onChange={onChangeGender}>
                                <option value=""></option>
                                <option value="male">{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__male')}</option>
                                <option value="female">{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__female')}</option>
                                {/*<option value="non-binary">{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__non_binary')}</option>*/}
                                {/*<option value="prefer-not-to-answer">{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__prefer_not_to_answer')}</option>*/}
                                <option value="other">{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__other')}</option>
                            </select>
                        </div>
                        { formGender === 'other' &&
                            <input className={'dq-register-txt'}
                                type='text'
                                name={'gender_other'}
                                id={'gender_other'}
                                style={{marginTop:".5vh"}}
                                value={formGenderOther}
                                placeholder={GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__other_specify')}
                                onChange={onChangeGenderOther}
                            />
                        }
                    </div>
                    {/* Always shown. When the school supplied it the learner must
                        still be able to say it is wrong — that is half of why they
                        are on this screen. */}
                    <div className='dq-register-row'>
                        <label>{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__year_of_birth')}</label>
                        <div className={'dq-select'}>
                            <select className={'dq-register-txt'} name={'yob'} id={'yob'} value={formYOB} onChange={onChangeYOB}>
                                <option value=""></option>
                                {
                                    birthYears.map( (year, index) =>
                                        <option value={year}>{year}</option>
                                    )
                                }
                            </select>
                        </div>
                    </div>
                    <div className='dq-register-row'>
                        <label>{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__country')}</label>
                        {/* PBDQ-45 — locked when the teacher set it in the class CSV. */}
                        <SearchableSelect
                            id={'country'}
                            name={'country'}
                            options={countryOptions}
                            value={formCountry}
                            onChange={handleCountryChange}
                            disabled={isCountryLocked}
                            placeholder=''
                            noResultsText={GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__no_results')}
                        />
                    </div>
                    { isStateLocked ? (
                    /* Teacher-set state: shown read-only. A plain input (not the
                       dropdown) so a CSV value with no matching code still displays. */
                    <div className='dq-register-row'>
                        <label>{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__state')}</label>
                        <input className={'dq-register-txt'}
                            type='text'
                            name={'state'}
                            id={'state'}
                            value={stateSelectOptions.find(s => s.value === formStateCode)?.label || teacherState}
                            disabled
                            readOnly
                        />
                    </div>
                    ) : stateOptions.length > 0 && (
                    <div className='dq-register-row'>
                        <label>{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__state')}</label>
                        <SearchableSelect
                            id={'state'}
                            name={'state'}
                            options={stateSelectOptions}
                            value={formStateCode}
                            onChange={handleStateChange}
                            placeholder=''
                            noResultsText={GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__no_results')}
                        />
                    </div>
                    )}
                    <div className='dq-register-row'>
                        <label>{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__postal_code')}</label>
                        <input className={'dq-register-txt'}
                            type='text'
                            name={'postal_code'}
                            id={'postal_code'}
                            value={formPostCode}
                            onChange={onChangePostCode}
                        />
                    </div>
                </div>
                </form>

                {/* The one thing on this screen a learner cannot work out for
                    themselves, and what stops them expecting their name to change
                    the moment they press Next. */}
                {isSchoolProvided && (
                <p className={'dq-demographic-note'} style={{
                    marginTop: 16, padding: '10px 14px', fontSize: 13, lineHeight: 1.5,
                    border: '1px dashed rgba(255,105,0,0.6)', borderRadius: 8,
                }}>
                    <strong>
                        {LC(pageLc, 'dq_profile_check__where_title')}
                    </strong>{' '}
                    {LC(pageLc, 'dq_profile_check__where_body')}
                </p>
                )}

                <div className={'dq-action-row'}>
                <div className={'dq-options-submit'}>
                {settings.theme === DQ_THEMES.NUS_THEME ? (
                    <button onClick={submitDemographicFrom}>{GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__get_score')}</button>
                ) :
                    <button onClick={submitDemographicFrom}>
                        {/* "Send to my school" only when every correction on this
                            form really is a correction. While required details are
                            still blank the learner is also completing their own,
                            and nothing may reach the school at all. */}
                        {isSchoolProvided && !needsCompletion
                            ? LC(pageLc, 'dq_profile_check__send')
                            : GET_APP_LOCALIZE_TEXT(pageLc, 'dq_demographic__next')}
                    </button>
                }
                </div>

                {/* Back to step 1. Only there when there is a step 1 to go back to. */}
                {isSchoolProvided && (
                <div className={'dq-options-submit dq-secondary-action'}>
                    <button onClick={() => {
                        setFormFirstName(original.first_name || '');
                        setFormLastName(original.last_name || '');
                        setFormStudentEmail(original.student_email || '');
                        setMode('check');
                    }}>
                        {LC(pageLc, 'dq_profile_check__cancel')}
                    </button>
                </div>
                )}
                </div>

                {/*<br />*/}
                {/*<br />*/}
                {/*<br />*/}
                {/*<a*/}
                {/*    className={'login-link'}*/}
                {/*    onClick={guestLogin}*/}
                {/*>Continue as Guest</a>*/}

                <Footer />
            </div>
        </>
    );
};

export default ProfileConflict;
